import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

const API_BASE = '/api/sites'

interface Competitor {
  id: string
  site_id: string
  competitor_domain: string
  label: string | null
  created_at: string
}

interface ComparisonRow {
  keywordId: string
  keyword: string
  searchVolume: number | null
  ourPosition: number | null
  competitors: Record<string, number | null>
}

interface ComparisonData {
  competitors: Array<{ id: string; domain: string; label: string | null }>
  comparison: ComparisonRow[]
}

interface VisibilityEntry {
  id: string
  domain: string
  label: string | null
  visibility: number
  shareOfVoice: number
}

interface VisibilityData {
  our: { visibility: number; shareOfVoice: number }
  competitors: VisibilityEntry[]
  totalVisibility: number
}

export function useCompetitors(siteId: string) {
  return useQuery({
    queryKey: ['competitors', siteId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/${siteId}/competitors`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch competitors')
      return res.json() as Promise<{ competitors: Competitor[] }>
    },
    enabled: !!siteId,
  })
}

export function useAddCompetitor(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { domain?: string; domains?: string[]; label?: string }) => {
      const res = await fetch(`${API_BASE}/${siteId}/competitors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to add competitor')
      return res.json() as Promise<{ added: string[]; count: number }>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['competitors', siteId] })
      qc.invalidateQueries({ queryKey: ['competitors', siteId, 'compare'] })
      qc.invalidateQueries({ queryKey: ['competitors', siteId, 'visibility'] })
    },
  })
}

export function useDeleteCompetitor(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (competitorId: string) => {
      const res = await fetch(`${API_BASE}/${siteId}/competitors/${competitorId}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete competitor')
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['competitors', siteId] })
      qc.invalidateQueries({ queryKey: ['competitors', siteId, 'compare'] })
      qc.invalidateQueries({ queryKey: ['competitors', siteId, 'visibility'] })
    },
  })
}

export function useCompetitorCompare(siteId: string) {
  return useQuery({
    queryKey: ['competitors', siteId, 'compare'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/${siteId}/competitors/compare`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch comparison')
      return res.json() as Promise<ComparisonData>
    },
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCompetitorVisibility(siteId: string) {
  return useQuery({
    queryKey: ['competitors', siteId, 'visibility'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/${siteId}/competitors/visibility`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch visibility')
      return res.json() as Promise<VisibilityData>
    },
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}
